import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getFullImageUrl, handleImageError } from '../utils/imageUtils';
import PublicChrome from './public/PublicChrome';
import DuskHero from './public/DuskHero';
import BlurFade from './public/motion/BlurFade';
import NetworkGrid, { sortNetworks } from './public/NetworkGrid';

interface Project {
  _id: string;
  title: string;
  slug: string;
  description: string;
  imageUrl: string;
  category: string;
  network: string;
  tags: string[];
  featured: boolean;
  apy?: string;
  projectUrl?: string;
  stakingLink?: string;
}

interface Blog {
  _id: string;
  title: string;
  description: string;
  slug: string;
  author: string;
  category: string;
  imageUrl?: string;
  createdAt: string;
  readTime: number;
}

/** In-app path when the URL is on this site; otherwise null. */
const sameOriginPath = (url: string): string | null => {
  if (!url) return null;
  if (url.startsWith('/')) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

const WHY_ITEMS = [
  {
    title: 'Proven validator reputation',
    text: 'Trusted by leading networks as a genesis and early-stage validator — consistent performance and long-term delegation support.',
  },
  {
    title: 'Fault-tolerant infrastructure',
    text: 'Distributed architecture across regions with enterprise-grade security, redundancy, and failover built in.',
  },
  {
    title: 'Non-custodial by design',
    text: 'You retain full control over your assets — no lockups, no intermediaries, no compromises on security.',
  },
  {
    title: 'Transparent operations',
    text: 'Clear metrics, public endpoints, and real-time status — you always know how your stake performs.',
  },
];

const MainSite: React.FC = () => {
  const [stakeProjects, setStakeProjects] = useState<Project[]>([]);
  const [featuredLabs, setFeaturedLabs] = useState<any[]>([]);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [blogPage, setBlogPage] = useState(0);
  const [zoomImage, setZoomImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const api = process.env.REACT_APP_API_URL || '';

        const [stakeRes, labsRes, blogsRes] = await Promise.allSettled([
          axios.get(`${api}/api/projects?category=Stake&status=active&isPublished=true`),
          axios.get(`${api}/api/labs?homepage=true&limit=50`),
          axios.get(`${api}/api/blogs?limit=9`),
        ]);

        if (stakeRes.status === 'fulfilled' && stakeRes.value.data.success) {
          setStakeProjects(sortNetworks(stakeRes.value.data.data.projects));
        }

        if (labsRes.status === 'fulfilled') {
          const labs = Array.isArray(labsRes.value.data)
            ? labsRes.value.data
            : labsRes.value.data.data || [];
          const homepageLabs = (Array.isArray(labs) ? labs : []).filter(
            (lab: any) => lab.featured === true || lab.superFeatured === true
          );
          setFeaturedLabs(homepageLabs);
        }

        if (blogsRes.status === 'fulfilled') {
          const bd = blogsRes.value.data;
          const arr = bd.data?.blogs || bd.data || [];
          setBlogs(Array.isArray(arr) ? arr : []);
        }
      } catch (err) {
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const perPage = 3;
  const totalPages = Math.max(1, Math.ceil(blogs.length / perPage));
  const visibleBlogs = blogs.slice(blogPage * perPage, (blogPage + 1) * perPage);

  return (
    <PublicChrome>
      <DuskHero />

      {/* Why */}
      <section className="dusk-section !py-20 md:!py-28">
        <div className="dusk-container">
          <BlurFade>
            <div className="max-w-xl mb-16 md:mb-20">
              <h2 className="dusk-section-title mb-5">Why DUSKPROOF?</h2>
              <p className="text-dusk-muted text-[1.05rem] leading-[1.7] m-0">
                An experienced PoS validator and staking provider — running multiple chains on
                top-level hardware, contributing to open-source, and offering tooling recognized by
                prominent crypto projects.
              </p>
            </div>
          </BlurFade>

          <div className="flex flex-col border-t border-[var(--dusk-line)]">
            {WHY_ITEMS.map((item, i) => (
              <BlurFade key={item.title} delay={0.06 * i}>
                <div className="grid md:grid-cols-[3.5rem_minmax(0,16rem)_1fr] gap-4 md:gap-12 py-10 md:py-12 border-b border-[var(--dusk-line)] items-start">
                  <span className="text-[0.7rem] font-semibold tracking-[0.18em] text-[var(--dusk-copper)] pt-1.5">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-display text-xl md:text-[1.35rem] text-dusk-ink m-0 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-dusk-muted text-[0.95rem] md:text-base leading-[1.7] m-0 max-w-xl">
                    {item.text}
                  </p>
                </div>
              </BlurFade>
            ))}
          </div>
        </div>
      </section>

      {/* Stake */}
      <section id="networks" className="dusk-section">
        <div className="dusk-container">
          <BlurFade>
            <h2 className="dusk-section-title mb-8">Stake</h2>
          </BlurFade>

          {loading ? (
            <p className="text-dusk-muted">Loading networks…</p>
          ) : stakeProjects.length === 0 ? (
            <p className="text-dusk-muted">No networks available yet.</p>
          ) : (
            <NetworkGrid items={stakeProjects} />
          )}
        </div>
      </section>

      {/* Products — only Labs marked favorite / super-favorite */}
      {featuredLabs.length > 0 && (
        <section id="products" className="dusk-section">
          <div className="dusk-container">
            <BlurFade>
              <h2 className="dusk-section-title">Our products</h2>
            </BlurFade>

            <div className="flex flex-col gap-0">
              {featuredLabs.map((lab: any, i: number) => (
                <BlurFade key={lab._id || i} delay={0.04 * i}>
                  <article className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] gap-8 md:gap-12 py-12 md:py-16 border-t border-[var(--dusk-line)] items-center">
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl text-dusk-ink m-0 mb-3">{lab.title}</h3>
                      <p className="text-dusk-muted leading-relaxed m-0 max-w-xl text-[0.95rem]">
                        {lab.description}
                      </p>
                      {(lab.primaryButton?.enabled && lab.primaryButton?.url) ||
                      (lab.secondaryButton?.enabled && lab.secondaryButton?.url) ? (
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5">
                          {lab.primaryButton?.enabled && lab.primaryButton?.url ? (
                            <a
                              href={lab.primaryButton.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block text-sm font-semibold tracking-wide uppercase text-dusk-copper no-underline hover:text-dusk-copper-deep"
                            >
                              {lab.primaryButton.text || 'View project'} →
                            </a>
                          ) : null}
                          {lab.secondaryButton?.enabled && lab.secondaryButton?.url
                            ? (() => {
                                const internal = sameOriginPath(lab.secondaryButton.url);
                                const label = `${lab.secondaryButton.text || 'READ MORE'} →`;
                                const cls =
                                  'inline-block text-sm font-semibold tracking-wide uppercase text-[var(--dusk-muted)] no-underline hover:text-[var(--dusk-ink)]';
                                // Same tab — article scroll-to-top is handled by ScrollToTop
                                return internal ? (
                                  <Link to={internal} className={cls}>
                                    {label}
                                  </Link>
                                ) : (
                                  <a href={lab.secondaryButton.url} className={cls}>
                                    {label}
                                  </a>
                                );
                              })()
                            : null}
                        </div>
                      ) : null}
                    </div>
                    {lab.imageUrl && (
                      <button
                        type="button"
                        aria-label={`Preview ${lab.title}`}
                        className="group justify-self-stretch md:justify-self-end w-full max-w-[28rem] p-0 border-0 bg-transparent cursor-zoom-in"
                        onClick={() => setZoomImage(getFullImageUrl(lab.imageUrl))}
                      >
                        <span
                          className="dusk-media-frame"
                          style={
                            {
                              ['--dusk-border-delay' as string]: `${-((i * 1.15) % 2.6)}s`,
                            } as React.CSSProperties
                          }
                        >
                          <span className="dusk-media-shot">
                            <img
                              src={getFullImageUrl(lab.imageUrl)}
                              alt={lab.title}
                              className="dusk-media-shot__img"
                              onError={handleImageError}
                            />
                          </span>
                        </span>
                      </button>
                    )}
                  </article>
                </BlurFade>
              ))}
              <hr className="dusk-divider" />
            </div>
          </div>
        </section>
      )}

      {/* Blog */}
      {blogs.length > 0 && (
        <section className="dusk-section">
          <div className="dusk-container">
            <BlurFade>
              <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <h2 className="dusk-section-title mb-0">Blog</h2>
                <Link to="/blog" className="text-sm font-semibold tracking-wide uppercase text-dusk-copper no-underline hover:text-dusk-copper-deep">
                  All posts →
                </Link>
              </div>
            </BlurFade>

            <div className="grid md:grid-cols-3 gap-8">
              {visibleBlogs.map((b, i) => (
                <BlurFade key={b._id} delay={0.05 * i}>
                  <article className="flex flex-col h-full border-t border-[var(--dusk-copper)] pt-5">
                    <p className="text-[0.7rem] font-semibold tracking-[0.12em] uppercase text-dusk-muted m-0 mb-2">
                      {b.author || 'DUSKPROOF'}
                    </p>
                    <h3 className="font-display text-xl text-dusk-ink m-0 mb-3 leading-snug">
                      {b.title}
                    </h3>
                    <p className="text-sm text-dusk-muted leading-relaxed flex-1 m-0 mb-4">
                      {b.description || b.title}
                    </p>
                    <Link
                      to={`/blog/${b.slug}`}
                      className="text-sm font-semibold tracking-wide uppercase text-dusk-ink no-underline hover:text-dusk-copper"
                    >
                      Read more →
                    </Link>
                  </article>
                </BlurFade>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-10">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setBlogPage(i)}
                    className={`h-2 w-2 rounded-sm border-0 cursor-pointer ${
                      i === blogPage ? 'bg-dusk-copper' : 'bg-dusk-mist'
                    }`}
                    aria-label={`Blog page ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {zoomImage && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-6 cursor-zoom-out bg-black/55 backdrop-blur-xl"
          onClick={() => setZoomImage(null)}
          role="presentation"
        >
          <button
            type="button"
            className="absolute top-4 right-4 z-10 text-dusk-ink text-3xl bg-transparent border-0 cursor-pointer"
            onClick={() => setZoomImage(null)}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={zoomImage}
            alt=""
            className="relative z-10 max-h-[88vh] max-w-5xl w-full object-contain rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </PublicChrome>
  );
};

export default MainSite;
